import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { SnackbarService, SnackbarStatus } from '@prism/angular-shared';
import { catchError, filter, first, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { AppwriteAuthService } from '../services/appwrite-auth.service';
import { UserApiService } from '../services/user-api.service';
import { CoreActions, CoreSelectors } from '../state/core';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {

    constructor(
        private auth: AppwriteAuthService,
        private router: Router,
        private snackbar: SnackbarService,
        private store: Store,
        private userApiService: UserApiService,
    ) { }

    public validate = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
        return this.auth.authState.pipe(
            first(),
            switchMap((auth) => {
                if (!auth) {
                    this.store.dispatch(CoreActions.ClearCurrentUser());
                    this.router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
                    this.snackbar.showSnackbar(SnackbarStatus.ERROR, 'please log in');
                    return of(false);
                }

                return this.store.select(CoreSelectors.getCurrentUser).pipe(
                    switchMap((user) => {
                        if (!!user) {
                            return of(user);
                        }
                        return this.userApiService.getUserById(auth.uid).pipe(
                            filter((u) => !!u),
                            tap((u) => this.store.dispatch(CoreActions.SetCurrentUser({ user: u }))),
                        );
                    }),
                    filter((user) => !!user),
                    first(),
                    map(() => true),
                    catchError(() => {
                        this.store.dispatch(CoreActions.ClearCurrentUser());
                        this.router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
                        this.snackbar.showSnackbar(SnackbarStatus.ERROR, 'please log in');
                        return of(false);
                    }),
                );
            }),
        );
    };

    canActivate(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ) {
        return this.validate(next, state);
    }

    canActivateChild(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ) {
        return this.validate(next, state);
    }

}
